/**
 * Sized against the host's DOM-mirroring agent, which fails to ship a `blob:`
 * url of a few MB across its `postMessage` RPC — see
 * `appendix/troubleshooting.md`. 640px at 0.7 lands a photo around 40–60KB,
 * well under the 150KB that is known to ship, and the widest tile showing a
 * preview is ~340 CSS px.
 */
const PREVIEW_MAX_EDGE = 640;
const PREVIEW_QUALITY = 0.7;
const PREVIEW_TYPE = 'image/jpeg';

/**
 * Under this, re-encoding buys nothing: 150KB is the largest payload measured
 * to cross the RPC intact, and the decode a shrink costs (~200–350ms for a
 * 12MP source on a mid Android) is time the preview spends not on screen.
 */
const PREVIEW_SKIP_BYTES = 150 * 1024;

const toObjectUrl = (file: Blob): string => URL.createObjectURL(file);

/**
 * A display-sized `blob:` url for a freshly picked file, to show while the real
 * upload runs. Callers own it exactly as they owned `URL.createObjectURL(file)`
 * before — same lifetime, same `revokeBlobUrl`.
 *
 * Falls back to a handle on the original bytes whenever it can't re-encode:
 * video (no still to draw), a file already small enough to ship as-is, a canvas
 * the browser won't give us, or a format `createImageBitmap` refuses — HEIC in
 * particular, which some WebViews decline even though the same file previews
 * fine in an `<img>`. The fallback is the old behaviour, so a file we can't
 * shrink is no worse off than before.
 */
const createPreviewUrl = async (file: Blob): Promise<string> => {
  if (!file.type.startsWith('image/')) return toObjectUrl(file);
  if (file.size <= PREVIEW_SKIP_BYTES) return toObjectUrl(file);

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(
      1,
      PREVIEW_MAX_EDGE / Math.max(bitmap.width, bitmap.height)
    );
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      return toObjectUrl(file);
    }

    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const preview = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, PREVIEW_TYPE, PREVIEW_QUALITY)
    );

    // Guard, not an expected path: with the skip threshold above, a re-encode
    // that lands bigger than its source is hard to construct. Cheap enough to
    // keep, since handing back a preview larger than the bytes we started with
    // is the one outcome this function must never have.
    return preview && preview.size < file.size
      ? toObjectUrl(preview)
      : toObjectUrl(file);
  } catch {
    // Deliberately not reported: a decode this browser won't do is an expected
    // outcome here, not a fault, and the fallback below is the behaviour that
    // shipped before previews were shrunk at all.
    return toObjectUrl(file);
  }
};

export default createPreviewUrl;
